import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { findFactory, findTool } from '../data/processControlSystem';
import './ProcessControlToolPage.css';

const SYSTEM_HOME = '/portal/supplier-process-control/system';

/**
 * Parameterized Process Control tool sub-page.
 *
 * Reads the `factory` and `tool` slugs from the route, resolves them to display
 * labels via the config helpers, and renders a breadcrumb + title + a
 * "content in progress" placeholder. If either slug is unknown, a friendly
 * fallback with a link back to the system home is shown instead.
 */
export function ProcessControlToolPage() {
  const { factory: factorySlug, tool: toolSlug } = useParams();
  const factory = findFactory(factorySlug);
  const tool = findTool(toolSlug);

  // Restyle the shared portal header into the Schnell-style dark bar, but only
  // while this page is mounted (scoped via a body class).
  useEffect(() => {
    document.body.classList.add('pct-schnell');
    return () => document.body.classList.remove('pct-schnell');
  }, []);

  if (!factory || !tool) {
    return (
      <div className="page pct">
        <div className="pct-notfound">
          <h1 className="pct-notfound__title">Tool not found</h1>
          <p className="pct-notfound__text">
            We couldn&apos;t find a Process Control tool for this address. It may
            have moved or the link is incorrect.
          </p>
          <Link to={SYSTEM_HOME} className="pct-notfound__link">
            ← Back to Process Control System
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page pct">
      {/* Breadcrumb */}
      <nav className="pct-crumbs" aria-label="Breadcrumb">
        <Link to={SYSTEM_HOME} className="pct-crumbs__link">
          Process Control System
        </Link>
        <span className="pct-crumbs__sep" aria-hidden>
          /
        </span>
        <Link to={SYSTEM_HOME} className="pct-crumbs__link">
          {factory.label}
        </Link>
        <span className="pct-crumbs__sep" aria-hidden>
          /
        </span>
        <span className="pct-crumbs__current" aria-current="page">
          {tool.label}
        </span>
      </nav>

      {/* Title */}
      <header className="pct-header">
        <h1 className="pct-header__title">
          {factory.label} · {tool.label}
        </h1>
        <p className="pct-header__subtitle">
          Process Control System — {factory.label} factory
        </p>
      </header>

      {/* Placeholder */}
      <section className="pct-placeholder">
        <div className="pct-placeholder__icon" aria-hidden>
          🚧
        </div>
        <h2 className="pct-placeholder__title">Content in progress</h2>
        <p className="pct-placeholder__text">
          The <strong>{tool.label}</strong> experience for{' '}
          <strong>{factory.label}</strong> is being built. Check back soon.
        </p>
      </section>
    </div>
  );
}
